import { useMemo } from "react";
import { BUILT_IN_VIEW_KEYS, BUILT_IN_VIEWS } from "./builtInViews";
import { describeView } from "./describeView";
import {
  ALL_VIEW_SECTIONS,
  parseSavedViewPayload,
  type ViewSections
} from "./savedViewPayload";
import { SavedViewPicker } from "./SavedViewPicker";
import type { ViewState } from "./useViewState";

/** What a save can carry, plus the default flag. */
const SAVE_TOGGLES = [
  { key: "dataShape", label: "Calendar, period, plan by, intake" },
  { key: "rows", label: "KPI rows and sorting" },
  { key: "layout", label: "Column layout and styling" },
  { key: "isDefault", label: "Set as default view" }
] as const;
const SECTION_KEYS = [
  "dataShape",
  "rows",
  "layout"
] as const satisfies readonly (keyof ViewSections)[];
const SAVE_DEFAULTS = { ...ALL_VIEW_SECTIONS, isDefault: false };
const BUILT_INS = BUILT_IN_VIEW_KEYS.map((key) => ({
  key,
  description: describeView(BUILT_IN_VIEWS[key])
}));

interface ViewSelectorProps {
  /** The page's Views layer (``useViewState``). */
  views: ViewState;
}

/**
 * The control that names the grid setup: the built-ins and the user's
 * own views in one list. The description under each row is derived from its
 * payload, so a row cannot promise what the view no longer does.
 */
export function ViewSelector({ views }: ViewSelectorProps) {
  const { builtInKeys } = views;
  const builtIns = useMemo(
    () => BUILT_INS.filter(({ key }) => builtInKeys.includes(key)),
    [builtInKeys]
  );
  return (
    <SavedViewPicker
      label="View"
      placeholder="Custom"
      builtIns={builtIns}
      views={views.views}
      describe={(view) => {
        const payload = parseSavedViewPayload(view.control_state);
        return payload ? describeView(payload) || "Layout only" : "Unreadable";
      }}
      activeView={views.activeView}
      activeSavedView={views.activeSavedView}
      isDirty={views.isDirty}
      onPickBuiltIn={views.applyBuiltIn}
      onPickSaved={views.applySavedView}
      writes={views}
      saveToggles={SAVE_TOGGLES}
      saveDefaults={SAVE_DEFAULTS}
      requireOneOf={SECTION_KEYS}
      onSave={(name, { isDefault, ...sections }) =>
        views.saveCurrentView(name, sections, isDefault)
      }
      onSaveOpenChange={views.setSaveOpen}
    />
  );
}
